import { useEffect, useState } from "react";
import { ShareExpiredPage, isExpiredError } from "@/components/ShareExpiry";
import { useParams } from "react-router-dom";
import axios from "axios";
import { daDate } from "@/lib/date";
import { AnnotationLayer } from "@/components/ImageAnnotator";
import { LayoutGrid, FileText, FileImage, ListChecks, CheckCircle2, XCircle, Building2, ShieldCheck, Wrench, History } from "lucide-react";
import { useProdLang } from "@/i18n/useProdLang";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABEL = { intern_qc: "Intern kvalitetskontrol", reklamation: "Reklamationsrapport", svejse_dok: "Svejsedokumentation", svejsning: "Svejsedokumentation", montage: "Montagedokumentation", slibning: "Slibningsdokumentation" };
const DOK_TYPES = ["svejsning", "montage", "slibning", "svejse_dok"];
const LEVEL = { Mindre: "#A8A29E", Væsentlig: "#A08979", Kritisk: "#A83232" };

const fmtUpdated = (iso, lang, atLabel) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const locale = lang === "ar" ? "ar" : "da-DK";
  const parts = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Copenhagen" }).formatToParts(d);
  const g = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${g("day")}-${g("month")}-${g("year")} ${atLabel} ${g("hour")}:${g("minute")}`;
};

const Section = ({ title, children, id }) => {
  const { lang } = useProdLang();
  const rtl = lang === "ar";
  return (
  <section id={id} className="mt-16 md:mt-24 scroll-mt-24" data-testid={`pub-qr-section-${id}`}>
    <h2 className={`text-2xl md:text-3xl font-semibold mb-6 ${rtl ? "" : "tracking-tight"}`} style={{ color: "#1A1919" }}>{title}</h2>
    {children}
  </section>
  );
};
const Card = ({ children, className = "", style }) => (
  <div className={`bg-white rounded-2xl border p-6 md:p-8 ${className}`} style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)", ...style }}>{children}</div>
);
const InfoField = ({ label, value, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ color: "#A8A29E" }}>{label}</p>
    {children || <p className="mt-1.5 text-base font-medium break-words [overflow-wrap:anywhere]" style={{ color: "#1A1919" }}>{value || "—"}</p>}
  </div>
);

function StatusBadge({ status, label }) {
  const map = {
    Godkendt: { c: "#7E856C", bg: "#E7EADF", bd: "#D3D8C6", bar: "#7E856C" },
    "Færdig": { c: "#7E856C", bg: "#E7EADF", bd: "#D3D8C6", bar: "#7E856C" },
    Afvist: { c: "#A83232", bg: "#F1DEDA", bd: "#E9C9C3", bar: "#A83232" },
    "I gang": { c: "#806B61", bg: "#F4EFEC", bd: "#E6DDD6", bar: "#A08979" },
    Kladde: { c: "#78716C", bg: "#F3F1EC", bd: "#E7E5E4", bar: "#A8A29E" },
  };
  const s = map[status] || map.Kladde;
  return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium" style={{ background: s.bg, color: s.c, border: `1px solid ${s.bd}` }} data-testid="pub-qr-status"><span className="w-1.5 h-1.5 rounded-full" style={{ background: s.bar }} />{label || status}</span>;
}

const initials = (name) => (name || "").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const sectionDefs = (rep, t) => {
  if (!rep) return [];
  const isRek = rep.type === "reklamation" || rep.type === "indkob_reklamation";
  const out = [];
  if (rep.description) out.push({ id: "desc", label: t("description"), icon: FileText });
  if ((rep.doc_images || []).length || (rep.doc_defects || []).length || (rep.doc_attachments || []).length) out.push({ id: "doc", label: t("documentation"), icon: FileImage });
  if (isRek && rep.corrective_action) out.push({ id: "act-corr", label: t("corr_action"), icon: Wrench });
  if (isRek && rep.preventive_action) out.push({ id: "act-prev", label: t("prev_action"), icon: ShieldCheck });
  out.push({ id: "concl", label: t("conclusion"), icon: CheckCircle2 });
  if ((rep.revision_history || []).length) out.push({ id: "hist", label: t("version_history"), icon: History });
  return out;
};

import { PrintPageStyle } from "@/components/print/PrintFrontMatter";
import { QualityPrintDoc } from "@/components/print/QualityPrint";
import { PrintButton } from "@/components/print/PrintButton";

export default function PublicQualityReport() {
  const { token, rid } = useParams();
  const fromShare = !!rid; // kundelink (låst part) → scoped endpoint
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState("top");
  const { lang, setLang, t } = useProdLang();
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    const m1 = document.createElement("meta"); m1.name = "referrer"; m1.content = "no-referrer"; const m2 = document.createElement("meta"); m2.name = "robots"; m2.content = "noindex, nofollow"; document.head.append(m1, m2);
    return () => { m1.remove(); m2.remove(); };
  }, []);
  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    if (lang === "ar" && !document.getElementById("gf-naskh")) {
      const l = document.createElement("link"); l.id = "gf-naskh"; l.rel = "stylesheet"; l.media = "print";
      l.href = "https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;500;600;700&display=swap";
      document.head.appendChild(l);
    }
    // Chrome tegner ikke url()-indhold i @page-marginbokse når <html dir="rtl"> – skift til ltr under selve udskriften (printvisningen har eget dir="rtl")
    const before = () => { if (lang === "ar") document.documentElement.dir = "ltr"; };
    const after = () => { document.documentElement.dir = lang === "ar" ? "rtl" : "ltr"; };
    window.addEventListener("beforeprint", before); window.addEventListener("afterprint", after);
    return () => { document.documentElement.dir = "ltr"; window.removeEventListener("beforeprint", before); window.removeEventListener("afterprint", after); };
  }, [lang]);

  useEffect(() => {
    if (!report || lang === "da" || !DOK_TYPES.includes(report.type) || fromShare) return;
    const need = ["title", "description", "conclusion_note"].some((k) => report[k] && !(report.tr?.[lang]?.[k]))
      || (report.doc_defects || []).some((d) => d.description && !(d.tr?.[lang]?.description))
      || (report.doc_images || []).some((im) => im.caption && !(im.tr?.[lang]?.caption));
    if (!need) return;
    setTranslating(true);
    axios.post(`${BACKEND}/api/public/quality-reports/${token}/ensure-translation?lang=${lang}`)
      .then((r) => setReport((prev) => ({ ...prev, tr: r.data.tr, doc_defects: r.data.doc_defects, doc_images: r.data.doc_images })))
      .catch(() => {})
      .finally(() => setTranslating(false));
  }, [lang, report?.id]); // eslint-disable-line

  useEffect(() => {
    document.title = "BSM A/S — Kvalitetsrapport";
    const id = "gf-manrope";
    if (!document.getElementById(id)) {
      const l = document.createElement("link"); l.id = id; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap";
      document.head.appendChild(l);
    }
    axios.get(fromShare ? `${BACKEND}/api/public/qr-shares/${token}/reports/${rid}` : `${BACKEND}/api/public/quality-reports/${token}`).then((r) => setReport(r.data)).catch((e) => setError(isExpiredError(e) ? "expired" : true));
  }, [token, rid]); // eslint-disable-line

  useEffect(() => {
    if (!report) return;
    const ids = ["top", ...sectionDefs(report, t).map((s) => s.id)];
    const onScroll = () => {
      let cur = "top";
      for (const id of ids) { const el = document.getElementById(id); if (el && el.getBoundingClientRect().top <= 140) cur = id; }
      setActive(cur);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [report]); // eslint-disable-line

  const font = { fontFamily: "Manrope, Inter, ui-sans-serif, system-ui, sans-serif" };

  if (error === "expired") return <ShareExpiredPage />;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: "#F3F1EC", ...font }} data-testid="pub-qr-error">
      <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#A83232" }}>BSM A/S</p>
      <h1 className="text-3xl font-light mt-4" style={{ color: "#1A1919" }}>Denne rapport er ikke længere delt.</h1>
      <p className="mt-3 max-w-md" style={{ color: "#A8A29E" }}>Kontakt din kontakt hos BSM A/S for at få et opdateret link.</p>
    </div>
  );
  if (!report) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBFAF8", color: "#A8A29E", ...font }}>{t("loading")}</div>;

  const imgUrl = (iid) => `${BACKEND}/api/quality-reports/${report.id}/images/${iid}?share=${token}`;
  const attUrl = (aid) => fromShare ? `${BACKEND}/api/public/qr-shares/${token}/reports/${rid}/attachments/${aid}` : `${BACKEND}/api/public/quality-reports/${token}/attachments/${aid}`;
  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const isRek = report.type === "reklamation" || report.type === "indkob_reklamation";
  const deptLabel = DOK_TYPES.includes(report.type) ? t("dept_produktion") : t("dept_kvalitet");
  const isDok = DOK_TYPES.includes(report.type);
  const rtl = lang === "ar";
  const trActive = isDok && lang !== "da";
  const tv = (k) => trActive ? ((report.tr?.[lang]?.[k]) ?? report[k]) : report[k];
  const icap = (im) => trActive ? ((im.tr?.[lang]?.caption) ?? im.caption) : im.caption;
  const ddesc = (d) => trActive ? ((d.tr?.[lang]?.description) ?? d.description) : d.description;
  const navItems = [{ id: "top", label: t("overview"), icon: LayoutGrid }, ...sectionDefs(report, t)];

  return (
    <div className="min-h-screen flex overflow-x-hidden pub-print-doc" style={{ background: "#FBFAF8", color: "#1A1919", ...font }} data-testid="pub-qr-canvas">
      <PrintPageStyle title={tv("title") || "Kvalitetsrapport"} date={daDate(report.revision_date || report.created_at)} rtl={rtl} pageWord={rtl ? "صفحة" : lang === "en" ? "Page" : "Side"} ofWord={rtl ? "من" : lang === "en" ? "of" : "af"} />
      <QualityPrintDoc report={report} token={token} lang={lang} t={t} tv={tv} icap={icap} ddesc={ddesc} sectionDefs={sectionDefs} />
      {/* Sidebar */}
      <aside className="no-print hidden lg:flex flex-col w-64 shrink-0 fixed inset-y-0 start-0 bg-white border-e z-30" style={{ borderColor: "#EBE8E3" }}>
        <div className="flex items-center px-6 h-20 shrink-0"><img src="/bsm-logo.png" alt="BSM A/S — vision in steel" className="h-9 w-auto" /></div>
        <div className="px-6 pb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#A8A29E" }}>{t("report_for")}</p>
          <p className="text-sm font-semibold mt-1 truncate" style={{ color: "#1A1919" }}>{report.customer_name || (isDok ? t(report.type === "montage" ? "tpl_montage_desc" : report.type === "slibning" ? "tpl_slibning_desc" : "tpl_svejsning_desc") : TYPE_LABEL[report.type])}</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {navItems.map((n) => {
            const A = active === n.id;
            return (
              <button key={n.id} onClick={() => go(n.id)} data-testid={`pub-qr-nav-${n.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-colors ${A ? "bg-[#F1DEDA] text-[#A83232]" : "text-stone-600 hover:bg-[#F3F1EC] hover:text-stone-900"}`}>
                <n.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} /> <span className="truncate">{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="border-t p-5 text-xs" style={{ borderColor: "#EBE8E3", color: "#A8A29E" }}>BSM A/S · {new Date().getFullYear()}</div>
      </aside>

      <div className="flex-1 lg:ms-64 print:ms-0 min-w-0 overflow-x-hidden">
        {/* Mobil top-bar */}
        <div className="no-print lg:hidden sticky top-0 z-20 bg-white/90 backdrop-blur border-b" style={{ borderColor: "#EBE8E3" }}>
          <div className="flex items-center justify-between px-5 h-16">
            <img src="/bsm-logo.png" alt="BSM A/S — vision in steel" className="h-7 w-auto" />
            {report.last_updated && <p className="text-[11px]" style={{ color: "#A8A29E" }}>{t("updated_short")} {fmtUpdated(report.last_updated, lang, t("at_time"))}</p>}
          </div>
          <div className="flex gap-2 overflow-x-auto px-5 pb-3">
            {navItems.map((n) => (
              <button key={n.id} data-testid={`pub-qr-mobnav-${n.id}`} onClick={() => go(n.id)} className={`whitespace-nowrap text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${active === n.id ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : "text-stone-600 border-[#EBE8E3]"}`}>{n.label}</button>
            ))}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-24">
          <div className="flex justify-end mb-2"><PrintButton lang={lang} filename={`${isRek ? "Reklamation" : isDok ? "Dokumentation" : "Kvalitetsrapport"} ${report.complaint_no || report.doc_no || ""}`.trim()} /></div>
          <div id="top" className="no-print scroll-mt-24 flex items-center justify-end gap-4 min-h-[1rem]">
            {isDok && <LanguageSwitcher lang={lang} setLang={setLang} />}
            {isDok && translating && <span className="text-xs animate-pulse" data-testid="pub-qr-translating" style={{ color: "#A8A29E" }}>{t("translating")}</span>}
            {report.last_updated && <p className="hidden lg:block text-xs" data-testid="pub-qr-last-updated" style={{ color: "#A8A29E" }}>{t("last_updated_label")} {fmtUpdated(report.last_updated, lang, t("at_time"))}</p>}
          </div>

          <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-x-10 gap-y-8 lg:gap-y-10 items-start">
            <div className="min-w-0 order-1">
              <p className={`text-xs font-semibold uppercase ${rtl ? "" : "tracking-[0.2em]"}`} style={{ color: "#A8A29E" }}>{isDok ? t(report.type === "montage" ? "tpl_montage_desc" : report.type === "slibning" ? "tpl_slibning_desc" : "tpl_svejsning_desc") : (TYPE_LABEL[report.type] || "Kvalitetsrapport")}</p>
              <h1 className={`mt-3 text-4xl md:text-5xl font-light break-words [overflow-wrap:anywhere] ${rtl ? "" : "tracking-tight"}`} data-testid="pub-qr-title" style={{ color: "#1A1919" }}>{tv("title") || "Kvalitetsrapport"}</h1>
            </div>

            {/* Ansvarlig-kort */}
            {!fromShare && <div className="w-full bg-white rounded-2xl border p-5 md:p-6 order-3 lg:order-2" style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)" }}>
              <p className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "#A83232" }}>{t("responsible")}</p>
              <div className="mt-4 flex items-center gap-4">
                {report.responsible_avatar_url
                  ? <img src={`${BACKEND}${report.responsible_avatar_url}`} alt={report.responsible_name || "Ansvarlig"} data-testid="pub-qr-avatar" className="h-16 w-16 rounded-full object-cover shrink-0" style={{ border: "1px solid #E9C9C3" }} />
                  : <div className="h-16 w-16 rounded-full shrink-0 flex items-center justify-center font-semibold" data-testid="pub-qr-avatar-fallback" style={{ background: "#F1DEDA", color: "#A83232", border: "1px solid #E9C9C3", fontSize: 20 }}>{initials(report.responsible_name) || "?"}</div>}
                <div className="min-w-0">
                  <p className="text-base font-semibold break-words" style={{ color: "#1A1919" }}>{report.responsible_name || "—"}</p>
                  <p className="text-sm mt-0.5" style={{ color: "#78716C" }}>BSM A/S · {deptLabel}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 text-sm border-t" style={{ borderColor: "#F3F1EC", color: "#78716C" }}>{t("version_label")} {report.revision || 1} · {daDate(report.revision_date || report.created_at)}</div>
            </div>}
            {fromShare && <div className="w-full bg-white rounded-2xl border p-5 md:p-6 order-3 lg:order-2" style={{ borderColor: "#EBE8E3" }} data-testid="pub-qr-share-card"><p className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "#A83232" }}>Reklamation</p><p className="mt-3 text-sm" style={{ color: "#78716C" }}>{t("version_label")} {report.revision || 1} · {daDate(report.revision_date || report.created_at)}</p><a href={report.back} className="mt-4 inline-block text-sm font-medium underline underline-offset-2" style={{ color: "#A83232" }} data-testid="pub-qr-back">← Tilbage til oversigten</a></div>}

            {/* Metadata */}
            <div className="min-w-0 order-2 lg:order-3 grid grid-cols-2 sm:grid-cols-3 gap-x-10 gap-y-7" data-testid="pub-qr-meta">
              {report.doc_no && <InfoField label={t("docno")} value={report.doc_no} />}
              {isRek && <InfoField label="Kunderef" value={report.customer_ref} />}
              {isRek && <InfoField label="Reklamationsnr" value={report.complaint_no} />}
              <InfoField label={t("varenr")} value={report.item_no} />
              <InfoField label={t("customer")} value={report.customer_name} />
              <InfoField label={t("date")} value={daDate(report.revision_date || report.created_at)} />
              <InfoField label={t("status_label")}><div className="mt-1.5"><StatusBadge status={report.status} label={report.status ? t(`status_${report.status}`) : t("not_set")} /></div></InfoField>
            </div>
          </div>

          {DOK_TYPES.includes(report.type) && (
            <div className="mt-10 md:mt-14 rounded-2xl border px-6 py-5" style={{ borderColor: "#E9C9C3", background: "rgba(251,237,237,0.6)" }} data-testid="pub-doc-fixed-note">
              <p className="text-base leading-relaxed" style={{ color: "#1A1919" }}>{t("bom_pre")}<strong className="font-semibold">{t("bom_strong")}</strong>{t("bom_post")}</p>
            </div>
          )}

          {tv("description") && (
            <Section id="desc" title={t("description")}>
              <Card><p className="text-base leading-relaxed whitespace-pre-wrap" style={{ color: "#78716C" }} data-testid="pub-qr-description">{tv("description")}</p></Card>
            </Section>
          )}

          {((report.doc_images || []).length > 0 || (report.doc_defects || []).length > 0 || (report.doc_attachments || []).length > 0) && (
            <Section id="doc" title={t("documentation")}>
              <div className="space-y-6">
                {(report.doc_images || []).map((im, i) => (
                  <div key={im.id || i} className="print-avoid">
                    <Card className="!p-3 md:!p-3">
                      <div className="relative rounded-xl overflow-hidden" style={{ border: "1px solid #EBE8E3" }}>
                        <img src={imgUrl(im.image_id)} alt={icap(im) || ""} className="block w-full h-auto" />
                        <AnnotationLayer annotations={im.annotations || []} />
                      </div>
                    </Card>
                    {icap(im) && <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "#78716C" }} data-testid="pub-qr-image-caption">{icap(im)}</p>}
                  </div>
                ))}
                {(report.doc_defects || []).length > 0 && (
                  <Card>
                    <div className="space-y-3">
                      {(report.doc_defects || []).map((d) => (
                        <div key={d.id} className="flex items-center gap-4 rounded-xl border px-4 py-3" style={{ borderColor: "#F3F1EC" }}>
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: LEVEL[d.level] || "#A8A29E" }} />
                          <span className="flex-1 min-w-0" style={{ color: "#1A1919" }}>{ddesc(d) || "—"}</span>
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: "#F3F1EC", color: "#78716C" }}>{d.level ? t(`level_${d.level}`) : ""}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
                {(report.doc_attachments || []).length > 0 && (
                  <Card>
                    <p className="text-sm font-semibold mb-3" style={{ color: "#1A1919" }}>Yderligere dokumentation</p>
                    <div className="space-y-2">
                      {(report.doc_attachments || []).map((f) => (
                        <a key={f.id} href={attUrl(f.id)} target="_blank" rel="noreferrer" data-testid={`pub-qr-attach-${f.id}`} className="flex items-center gap-3 rounded-xl border px-4 py-3 hover:bg-[#FBFAF8] transition-colors" style={{ borderColor: "#F3F1EC" }}>
                          <FileText className="h-4 w-4 shrink-0" style={{ color: "#A8A29E" }} />
                          <span className="flex-1 min-w-0 truncate" style={{ color: "#1A1919" }}>{f.original_filename}</span>
                          <span className="text-xs shrink-0" style={{ color: "#A8A29E" }}>{Math.max(1, Math.round((f.size || 0) / 1024))} KB</span>
                        </a>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            </Section>
          )}

          {isRek && report.corrective_action && (
            <Section id="act-corr" title={t("corr_action")}>
              <Card><p className="leading-relaxed whitespace-pre-wrap" style={{ color: "#78716C" }} data-testid="pub-qr-act-corr">{report.corrective_action}</p></Card>
            </Section>
          )}
          {isRek && report.preventive_action && (
            <Section id="act-prev" title={t("prev_action")}>
              <Card><p className="leading-relaxed whitespace-pre-wrap" style={{ color: "#78716C" }} data-testid="pub-qr-act-prev">{report.preventive_action}</p></Card>
            </Section>
          )}

          {(() => {
            const done = report.status === "Godkendt" || report.status === "Færdig";
            const bad = report.status === "Afvist";
            const prog = report.status === "I gang";
            const col = bad ? "#A83232" : done ? "#7E856C" : prog ? "#806B61" : "#A8A29E";
            const bd = bad ? "#E9C9C3" : done ? "#D3D8C6" : prog ? "#E6DDD6" : "#EBE8E3";
            return (
              <Section id="concl" title={t("conclusion")}>
                <Card style={{ borderColor: bd, boxShadow: "0 12px 48px rgba(0,0,0,0.05)" }}>
                  <div className="flex items-center gap-3">
                    {bad ? <XCircle className="h-6 w-6" style={{ color: "#A83232" }} /> : done ? <CheckCircle2 className="h-6 w-6" style={{ color: "#7E856C" }} /> : <FileText className="h-6 w-6" style={{ color: prog ? "#806B61" : "#A8A29E" }} />}
                    <h3 className="text-xl font-semibold" style={{ color: col }}>{report.status ? t(`status_${report.status}`) : t("not_set")}</h3>
                  </div>
                  {tv("conclusion_note") && <p className="mt-4 leading-relaxed whitespace-pre-wrap" style={{ color: "#78716C" }}>{tv("conclusion_note")}</p>}
                </Card>
              </Section>
            );
          })()}

          {(report.revision_history || []).length > 0 && (
            <Section id="hist" title={t("version_history")}>
              <Card>
                <div className="relative">
                  {[...report.revision_history].reverse().map((h, i, arr) => (
                    <div key={i} className="flex gap-4 pb-6 last:pb-0" data-testid={`pub-qr-hist-${h.version}`}>
                      <div className="flex flex-col items-center shrink-0">
                        <div className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0" style={{ background: "#F1DEDA", color: "#A83232", border: "1px solid #E9C9C3" }}>{h.version}</div>
                        {i < arr.length - 1 && <div className="w-px flex-1 mt-1" style={{ background: "#EBE8E3" }} />}
                      </div>
                      <div className="min-w-0 pt-1">
                        <p className="text-sm font-semibold" style={{ color: "#1A1919" }}>{t("version_label")} {h.version}</p>
                        <p className="text-base leading-relaxed mt-1 whitespace-pre-wrap" style={{ color: "#78716C" }}>{h.note || t("no_version_note")}</p>
                        <p className="text-xs mt-1.5" style={{ color: "#A8A29E" }}>{daDate(h.at)}{h.by ? ` · ${h.by}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Section>
          )}

          <footer className="mt-20 pt-8 border-t text-sm flex items-center gap-2" style={{ borderColor: "#EBE8E3", color: "#A8A29E" }} data-testid="pub-qr-footer">
            <Building2 className="h-4 w-4" strokeWidth={1.5} /> BSM A/S · {deptLabel} · {t("readonly_view")}
          </footer>
        </div>
      </div>
    </div>
  );
}
